// User Management Service

import { apiClient } from './apiClient';
import {
  User,
  CreateUserRequest,
  UpdateUserRequest,
  Role,
  RoleDetails,
  CreateRoleRequest,
  UpdateRoleRequest,
  UpdateRolePermissionsRequest,
  Permission,
  CreatePermissionRequest,
  UpdatePermissionRequest,
  PermissionMatrix,
  PersonForLinking,
  LinkUserToPersonRequest
} from '../types/userManagement.types';

class UserManagementService {
  // Users
  async getUsers(): Promise<User[]> {
    const response = await apiClient.get('/usermanagement/users');
    return response.data;
  }

  async getUser(id: string): Promise<User> {
    const response = await apiClient.get(`/usermanagement/users/${id}`);
    return response.data;
  }

  async createUser(data: CreateUserRequest): Promise<User> {
    const response = await apiClient.post('/usermanagement/users', data);
    return response.data;
  }

  async updateUser(id: string, data: UpdateUserRequest): Promise<User> {
    const response = await apiClient.put(`/usermanagement/users/${id}`, data);
    return response.data;
  }

  async deleteUser(id: string): Promise<void> {
    await apiClient.delete(`/usermanagement/users/${id}`);
  }

  async resetUserPassword(id: string, newPassword: string): Promise<{ message: string }> {
    const response = await apiClient.post(`/usermanagement/users/${id}/reset-password`, { newPassword });
    return response.data;
  }

  // Person Linking
  async getPersonsForLinking(): Promise<PersonForLinking[]> {
    const response = await apiClient.get('/usermanagement/persons-for-linking');
    return response.data;
  }

  async linkUserToPerson(userId: string, data: LinkUserToPersonRequest): Promise<void> {
    await apiClient.post(`/usermanagement/users/${userId}/link-person`, data);
  }

  // Roles
  async getRoles(): Promise<Role[]> {
    const response = await apiClient.get('/usermanagement/roles');
    return response.data;
  }

  async getRole(id: string): Promise<RoleDetails> {
    const response = await apiClient.get(`/usermanagement/roles/${id}`);
    return response.data;
  }

  async createRole(data: CreateRoleRequest): Promise<Role> {
    const response = await apiClient.post('/usermanagement/roles', data);
    return response.data;
  }

  async updateRole(id: string, data: UpdateRoleRequest): Promise<Role> {
    const response = await apiClient.put(`/usermanagement/roles/${id}`, data);
    return response.data;
  }

  async deleteRole(id: string): Promise<void> {
    await apiClient.delete(`/usermanagement/roles/${id}`);
  }

  async updateRolePermissions(id: string, data: UpdateRolePermissionsRequest): Promise<void> {
    await apiClient.put(`/usermanagement/roles/${id}/permissions`, data);
  }

  // Permissions
  async getPermissions(): Promise<Permission[]> {
    const response = await apiClient.get('/usermanagement/permissions');
    return response.data;
  }

  async getPermission(id: string): Promise<Permission> {
    const response = await apiClient.get(`/usermanagement/permissions/${id}`);
    return response.data;
  }

  async createPermission(data: CreatePermissionRequest): Promise<Permission> {
    const response = await apiClient.post('/usermanagement/permissions', data);
    return response.data;
  }

  async updatePermission(id: string, data: UpdatePermissionRequest): Promise<Permission> {
    const response = await apiClient.put(`/usermanagement/permissions/${id}`, data);
    return response.data;
  }

  async deletePermission(id: string): Promise<void> {
    await apiClient.delete(`/usermanagement/permissions/${id}`);
  }

  // Permission Matrix
  async getPermissionMatrix(): Promise<PermissionMatrix> {
    const response = await apiClient.get('/usermanagement/permission-matrix');
    return response.data;
  }

  // Initialize Menu Permissions
  async initializeMenuPermissions(): Promise<{ message: string; added: number; skipped: number; total: number }> {
    const response = await apiClient.post('/usermanagement/initialize-menu-permissions');
    return response.data;
  }

  // Get Current User Permissions
  async getMyPermissions(): Promise<string[]> {
    const response = await apiClient.get('/usermanagement/my-permissions');
    return response.data;
  }
}

export const userManagementService = new UserManagementService();
